import { createContext, useContext, useEffect, useRef, useState, useCallback, Fragment } from "react";
import { IT_EN, IT_EN_WORDS, genderizeText } from "@/lib/i18nDict";

const LangContext = createContext(null);
const STORAGE_KEY = "pc_lang";

const WORD_RE = new RegExp("\\b(" + Object.keys(IT_EN_WORDS).join("|") + ")\\b", "gi");

function translateWords(text) {
  if (!WORD_RE.test(text)) return text;
  WORD_RE.lastIndex = 0;
  return text.replace(WORD_RE, (m) => {
    const rep = IT_EN_WORDS[m.toLowerCase()];
    if (!rep) return m;
    if (m.length > 1 && m === m.toUpperCase()) return rep.toUpperCase();
    if (m[0] === m[0].toUpperCase()) return rep[0].toUpperCase() + rep.slice(1);
    return rep;
  });
}

function detectInitialLang() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "it" || stored === "en") return stored;
  } catch (e) { /* ignore */ }
  try {
    const langs = navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language || "en"];
    const isItalian = langs.some((l) => (l || "").toLowerCase().startsWith("it"));
    return isItalian ? "it" : "en";
  } catch (e) {
    return "en";
  }
}

const ATTRS = ["placeholder", "title", "aria-label"];

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(detectInitialLang);
  const observerRef = useRef(null);
  const rafRef = useRef(0);

  // Transform matching text nodes + attributes: IT->EN when English, and
  // always normalise Italian terminology to gender-neutral/universal-masculine form.
  const applyTransforms = useCallback((root) => {
    const doEn = lang === "en";
    if (!root) return;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    const skipTx = (n) => { const el = n.nodeType === 1 ? n : n.parentElement; return !!(el && el.closest && el.closest('[translate="no"]')); };
    for (const n of nodes) {
      const raw = n.nodeValue;
      if (!raw) continue;
      const key = raw.trim();
      if (!key) continue;
      if (skipTx(n)) continue;
      let out = raw;
      if (doEn) {
        const en = IT_EN[key];
        if (en && en !== key) {
          const lead = raw.slice(0, raw.length - raw.trimStart().length);
          const trail = raw.slice(raw.trimEnd().length);
          out = lead + en + trail;
        } else {
          out = translateWords(raw);
        }
      }
      out = genderizeText(out);
      if (out !== raw) n.nodeValue = out;
    }
    const els = root.nodeType === 1 ? [root, ...root.querySelectorAll("[placeholder],[title],[aria-label]")] : root.querySelectorAll("[placeholder],[title],[aria-label]");
    els.forEach((el) => {
      if (!el.getAttribute) return;
      ATTRS.forEach((attr) => {
        const v = el.getAttribute(attr);
        if (!v) return;
        let out = doEn && IT_EN[v.trim()] ? IT_EN[v.trim()] : v;
        out = genderizeText(out);
        if (out !== v) el.setAttribute(attr, out);
      });
    });
  }, [lang]);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, lang); } catch (e) { /* ignore */ }
    document.documentElement.setAttribute("lang", lang);

    if (observerRef.current) { observerRef.current.disconnect(); observerRef.current = null; }
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = 0; }

    const run = () => applyTransforms(document.body);
    rafRef.current = requestAnimationFrame(() => requestAnimationFrame(run));

    const obs = new MutationObserver((mutations) => {
      let pending = false;
      for (const m of mutations) {
        if (m.type === "childList" && m.addedNodes.length) { pending = true; break; }
        if (m.type === "characterData") { pending = true; break; }
        if (m.type === "attributes") { pending = true; break; }
      }
      if (!pending) return;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(run);
    });
    obs.observe(document.body, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ATTRS });
    observerRef.current = obs;

    return () => {
      if (observerRef.current) { observerRef.current.disconnect(); observerRef.current = null; }
      if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = 0; }
    };
  }, [lang, applyTransforms]);

  const setLang = useCallback((l) => setLangState(l === "en" ? "en" : "it"), []);
  // tr(it, en): explicit per-call translation (used by already-wrapped pages).
  const tr = useCallback((it, en) => {
    let s = lang === "en" ? (en ?? IT_EN[it] ?? it) : it;
    s = genderizeText(s);
    return s;
  }, [lang]);

  // Remount the whole tree on language change so switching back restores native markup.
  return (
    <LangContext.Provider value={{ lang, setLang, tr }}>
      <Fragment key={lang}>{children}</Fragment>
    </LangContext.Provider>
  );
}

export function useLang() {
  const ctx = useContext(LangContext);
  return ctx || { lang: "it", setLang: () => {}, tr: (it) => it, gender: "female" };
}
